import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { hideContextMenu } from '../contextMenuActions'
import { contextMenuItemClicked } from 'aria-viewer/analytics/analyticActions'
import styleguide from 'aria-viewer/tour/constants/styleGuide'
import colors from 'aria-viewer/tour/constants/style/colors'
import Icon from 'aria-viewer/tour/components/icon'
import Browser from 'aria-viewer/utils/mobileDetect'
import { properCase } from 'aria-viewer/utils/functions'
import mapSelectorsToProps from 'aria-viewer/core/selectors/mapSelectorsToProps'
import Radium from 'radium'
import isEqual from 'lodash-es/isEqual'

import LanguageButton from 'aria-viewer/tour/components/buttons/languageButton'
import DesktopShareButton from 'aria-viewer/tour/components/buttons/desktopShareButton'
import { showTourLanguageContextMenu, showShareContextMenu } from 'aria-viewer/contextmenu/contextMenuActions'
import { SCREEN_SIZES } from 'aria-viewer/tour/constants'

import ZIndexes from 'aria-viewer/tour/constants/ZIndexes'

const StandardButtonStyle = {
    ...styleguide.buttons.desktop_rounded_button,
    width: '100%',
    height: '2.45em',
    marginLeft: 0,
    background: 'transparent',
    boxShadow: 'none',
    borderRadius: 0,
    transition: 'none',
    ':hover': {
        transform: `none`,
        background: colors.dark_blue,
    },
}

const StandardButtonStyleLang = {
    ...styleguide.buttons.desktop_rounded_button,
    width: '100% !important',
    height: '2.45em',
    marginLeft: 0,
    background: 'transparent',
    boxShadow: 'none',
    borderRadius: 0,
    transition: 'none',
    ':hover': {
        transform: `none`,
        background: colors.dark_blue,
    },
}

const S = {
    container: {
        position: 'absolute',
        top: '0',
        right: '0',
        left: '0',
        bottom: '0',
        zIndex: ZIndexes.CONTEXT_MENU,
        transform: 'translateZ(1px)',
    },
    tooltipArrow: {
        width: 0,
        height: 0,
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderBottom: `10px solid ${colors.grey_1}`,
        display: 'inline',
    },
    tooltipContainer: {
        position: 'absolute',
        width: '100%',
        top: -10,
        textAlign: 'center',
    },
    tooltipInVR: {
        textAlign: 'right',
        right: '9%',
    },
    displayIconContainer: {
        display: 'block',
        width: '100%',
        height: '100%',
    },
}

class Tag extends PureComponent {
    constructor(props) {
        super(props)
        this.state = { event: null }
    }

    handleKeyPress = e => {
        if (e.key == 'Enter') {
            this.handleOnClick(e)
        }
    }
    handleOnClick = e => {
        this.setState({ event: e.type })
        this.props.onClick && this.props.onClick(e, this.props.item)
    }

    handleOnTouchStart = e => {
        this.setState({ event: e.type })
        this.props.onTouchStart && this.props.onTouchStart(e, this.props.item)
    }

    handleOnTouchEnd = e => {
        this.setState({ event: e.type })
        this.props.onTouchEnd && this.props.onTouchEnd(e, this.props.item)
    }

    handleOnMouseOver = e => {
        this.setState({ event: e.type })
        this.props.onMouseOver && this.props.onMouseOver(e, this.props.item)
    }

    handleOnMouseLeave = e => {
        this.setState({ event: e.type })
        this.props.onMouseLeave && this.props.onMouseLeave(e, this.props.item)
    }

    handleOnMouseDown = e => {
        this.setState({ event: e.type })
        this.props.onMouseDown && this.props.onMouseDown(e, this.props.item)
    }

    render() {
        const { options, item, theme, children, tabIndex } = this.props

        const Tag = item.tag || 'div'

        //setting events since firefox don't register events globally by default
        const event = event || this.state.event || window.event

        return (
            <Tag
                tabIndex={tabIndex}
                onClick={this.handleOnClick}
                onKeyPress={this.handleKeyPress}
                onTouchStart={this.handleOnTouchStart}
                onTouchEnd={this.handleOnTouchEnd}
                onMouseOver={this.handleOnMouseOver}
                onMouseLeave={this.handleOnMouseLeave}
                onMouseDown={this.handleOnMouseDown}
                style={Object.assign(
                    {},
                    theme.item,
                    options.getItemStyle ? options.getItemStyle(event, item) : {},
                    isEqual(options.selected, item) ? theme.currentItem : {}
                )}
                {...item.tagProps}
                className={item.className}
                title={properCase(item.className)}
                alt={item.className}
            >
                {children}
            </Tag>
        )
    }
}

const RadiumTag = Radium(Tag)

class ContextMenu extends PureComponent {
    static contextTypes = {
        width: PropTypes.number,
        height: PropTypes.number,
    }

    handleKeyPress = e => {
        if (e.key == 'Enter') {
            this.handleOnClick(e)
        }
    }

    handleOnClick = e => {
        stopAndPrevent(e)
        this.props.hideContextMenu()
    }

    handleOnClickTag = (e, item) => {
        const { onClick, options, hideContextMenu, contextMenuItemClicked } = this.props

        item.className && contextMenuItemClicked({ itemName: item.className })

        if (onClick) return onClick(e)

        item.action ? item.action(item) : options.defaultAction && options.defaultAction(item)
    }

    handleOnTouchMove = e => {
        this.props.hideContextMenu()
    }

    handleOnTouchStart = (e, item) => {
        const { options } = this.props

        if (options.onTouchStart) {
            options.onTouchStart(item)
        }
    }

    handleOnTouchEnd = (e, item) => {
        const { options } = this.props

        if (options.onTouchEnd) {
            options.onTouchEnd(item)
        }
    }

    handleOnMouseOver = (e, item) => {
        this.props.options.onMouseOver && this.props.options.onMouseOver(item)
    }

    handleOnMouseLeave = (e, item) => {
        this.props.options.onMouseLeave && this.props.options.onMouseLeave(item)
    }

    handleOnMouseDown = (e, item) => {
        this.props.options.onMouseDown && this.props.options.onMouseDown(item)
    }

    getTitle = (title, theme) => {
        if (title) {
            return (
                <div style={theme.title}>
                    <div style={theme.titleLabel}>{title}</div>
                </div>
            )
        }
    }

    getDisplayIcon = icon => {
        if (icon) {
            if (icon.link) {
                return (
                    <a className={icon.link.className} style={S.displayIconContainer}>
                        <Icon icon={icon.name} style={icon.style} />
                    </a>
                )
            } else {
                return <Icon icon={icon.name} style={icon.style} />
            }
        }
    }

    getDisplayText = (display, style) => {
        if (display) {
            return <label style={style}>{display}</label>
        }
    }

    getTooltipArrow = () => {
        const { isVrModuleEnabled } = this.props

        return (
            <div style={Object.assign({}, S.tooltipContainer, !isVrModuleEnabled ? S.tooltipInVR : {})}>
                <div style={S.tooltipArrow} />
            </div>
        )
    }

    showTourLanguageContextMenu = event => {
        this.props.showTourLanguageContextMenu({
            x: event.x + 35,
            y: event.y + 80,
        })
    }

    showShareContextMenu = event => {
        this.props.showShareContextMenu({
            x: event.x - 30,
            y: event.y + 45,
        })
    }

    showContextMenu = () => {
        const {
            options,
            theme,
            contextMenu,
            style,
            desktop,
            isVrModuleEnabled,
            language,
            viewportWidth,
            allowScroll,
        } = this.props

        let languageShortName = language ? language.getIn(['current', 'shortname']) : ''
        const isSmallWidth = viewportWidth <= SCREEN_SIZES.size_945

        let x = contextMenu.get('x') > this.context.width / 2 ? -100 : 0,
            y = contextMenu.get('y') > this.context.height / 2 ? -100 : 0

        if (desktop) {
            ;(x = isVrModuleEnabled ? -50 : -82), (y = 21)
        }
        let transform = `translate(${x}%, ${y}%)`

        return (
            <div
                id="share_menu"
                style={Object.assign(
                    {},
                    theme.container,
                    {
                        zIndex: 3000,
                        left: contextMenu.get('x') - 5,
                        top: contextMenu.get('y'),
                        overflow: 'none',
                        height: 'auto',
                        transform,
                    },
                    style
                )}
            >
                {desktop ? this.getTooltipArrow() : null}
                {this.getTitle(options.title, theme)}
                <div onTouchMove={stop} style={Browser.phone() && theme.scroller}>
                    {options.list.map((item, index) => {
                        switch (item.title) {
                            case 'Language':
                                return (
                                    <LanguageButton
                                        onClick={this.showTourLanguageContextMenu}
                                        theme="desktop"
                                        style={StandardButtonStyleLang}
                                        language={languageShortName}
                                        contextMenu={true}
                                    />
                                )
                            case 'Share':
                                return (
                                    <DesktopShareButton
                                        style={StandardButtonStyle}
                                        onClick={this.showShareContextMenu}
                                        offsetY={-185}
                                    />
                                )
                            default:
                                return (
                                    <RadiumTag
                                        tabIndex={this.props.tabIndex}
                                        key={index}
                                        onKeyPress={this.handleKeyPress}
                                        onClick={this.handleOnClickTag}
                                        onTouchStart={this.handleOnTouchStart}
                                        onTouchEnd={this.handleOnTouchEnd}
                                        onMouseOver={this.handleOnMouseOver}
                                        onMouseLeave={this.handleOnMouseLeave}
                                        onMouseDown={this.handleOnMouseDown}
                                        options={options}
                                        item={item}
                                        theme={theme}
                                    >
                                        {this.getDisplayIcon(item.icon)}
                                        {this.getDisplayText(item.display, theme.label)}
                                    </RadiumTag>
                                )
                        }
                    })}
                </div>
            </div>
        )
    }

    render() {
        return (
            <div onClick={this.handleOnClick} onTouchMove={this.handleOnTouchMove} style={S.container}>
                {this.showContextMenu()}
            </div>
        )
    }
}

ContextMenu.defaultProps = {
    allowScroll: false,
    desktop: false,
}

const stopAndPrevent = e => {
    e.stopPropagation()
    e.preventDefault()
}

const stop = e => {
    e.stopPropagation()
}

export default connect(
    mapSelectorsToProps(['contextMenu', 'isVrModuleEnabled', 'language', 'viewportWidth']),
    { hideContextMenu, contextMenuItemClicked, showTourLanguageContextMenu, showShareContextMenu },
    (stateProps, dispatchProps, ownProps) => ({ ...stateProps, ...dispatchProps, ...ownProps }),
    { withRef: true }
)(ContextMenu)
